import { BaseHead, CellOfRow } from '@components/tables';
import { Paging } from '@Core';
import { CameraTask, TaskPriority, TaskStatus } from '@EcamModel/model/CameraTask';
import {
    Autocomplete,
    Box,
    Stack,
    SxProps,
    Table,
    TableBody,
    TableCellProps,
    TableContainer,
    TableHead,
    TableRow,
    TextField,
    Theme,
    Tooltip,
    Typography,
} from '@mui/material';
import { DesktopDatePicker } from '@mui/x-date-pickers';
import { validateDatePickerValue } from '@pages/cameras/add-new';
import { FormValues, Mode, OverdueButton } from '@pages/overview/components/PopUpTaskInUnCompleteCamera';
import { autocompleteStyles } from '@pages/overview/smartFilterHighLight/components/BaseAutoCompleteForTypeMapping';
import { ellipsisStyles, scrollbarStyles } from '@pages/task-management';
import ActionButtons from '@pages/task-management/components/ActionButtons';
import StatusPriorityDropdown from '@pages/task-management/components/StatusPriorityDropdown';
import {
    cameraPriorityTask,
    cameraStatusTask,
    DropdownType,
    mappingPriorityTask,
    mappingStatusTask,
} from '@pages/task-management/config';
import moment from 'moment';
import { Dispatch, KeyboardEvent, SetStateAction } from 'react';
import { Control, Controller, UseFormHandleSubmit } from 'react-hook-form';
import { formatUKDate } from 'src/helpers';

type TableColumnTaskInComplete = {
    align: TableCellProps['align'];
    title: string;
    sx: SxProps<Theme>;
};

type Props = {
    control: Control<FormValues, any>;
    disabled: boolean;
    taskItem: CameraTask;
    mode: Mode;
    listTasks: Paging<CameraTask>;
    onSetListTask: Dispatch<SetStateAction<Paging<CameraTask>>>;
    setMode: (value: SetStateAction<Mode>) => void;
    setTaskItem: Dispatch<SetStateAction<CameraTask>>;
    handleSubmit: UseFormHandleSubmit<FormValues>;
    handleActionPressEnterKey: (event: KeyboardEvent<HTMLTableSectionElement>) => void;
    handleConfirmUpsertTask: (data: FormValues) => Promise<void>;
    handleChangeStatusOrPriority: (
        selectedItem: TaskStatus | TaskPriority | null,
        taskId: number,
        type: DropdownType
    ) => void;
};
export default function TableListTaskInComplete(props: Props) {
    const {
        control,
        disabled,
        taskItem,
        mode,
        listTasks,
        onSetListTask,
        setMode,
        setTaskItem,
        handleSubmit,
        handleActionPressEnterKey,
        handleChangeStatusOrPriority,
        handleConfirmUpsertTask,
    } = props;

    const tableColumns: TableColumnTaskInComplete[] = [
        { align: 'left', title: 'Task', sx: { minWidth: 'max-content' } },
        { align: 'center', title: 'Status', sx: { minWidth: 'max-content' } },
        { align: 'center', title: 'Priority', sx: { minWidth: 'max-content' } },
        { align: 'left', title: 'Assign to', sx: { minWidth: 'max-content' } },
        { align: 'center', title: 'Due date', sx: { minWidth: '120px' } },
        { align: 'left', title: 'Created by', sx: { minWidth: '150px' } },
        { align: 'center', title: 'Start date', sx: { minWidth: '120px' } },
        { align: 'right', title: '', sx: { minWidth: 'max-content' } },
    ];

    return (
        <TableContainer
            component={Box}
            sx={{
                maxHeight: 600,
                overflow: 'auto',
                '& .chart-scroll-bar': scrollbarStyles,
                '&::-webkit-scrollbar': {
                    width: '4px',
                },
                '::-webkit-scrollbar-track': {
                    background: 'transparent',
                },
            }}
        >
            <Table stickyHeader aria-label="simple table">
                <TableHead>
                    <TableRow>
                        {tableColumns.map((column, index) => (
                            <BaseHead key={index} align={column.align} title={column.title} sx={column.sx} />
                        ))}
                    </TableRow>
                </TableHead>
                <TableBody
                    onKeyDown={(e) => {
                        handleActionPressEnterKey(e);
                    }}
                >
                    {listTasks.rows?.map((task, index) => {
                        const isSameIdTask = taskItem.Id === task.Id;
                        const isEditing =
                            (mode === Mode.edit && isSameIdTask) || (mode === Mode.create && isSameIdTask);

                        return (
                            <TableRow key={index}>
                                {isEditing ? (
                                    <CellOfRow
                                        value={
                                            <Stack width={'100%'}>
                                                <Controller
                                                    name="taskName"
                                                    control={control}
                                                    rules={{
                                                        required: {
                                                            value: true,
                                                            message: 'Please enter a task.',
                                                        },
                                                    }}
                                                    render={({ field, fieldState: { error } }) => (
                                                        <TextField
                                                            {...field}
                                                            required
                                                            autoFocus
                                                            placeholder={'Task'}
                                                            label="Task"
                                                            error={!!error?.message}
                                                            helperText={error?.message}
                                                            value={field.value}
                                                        />
                                                    )}
                                                />
                                            </Stack>
                                        }
                                        align="center"
                                    />
                                ) : (
                                    <CellOfRow
                                        value={
                                            <Tooltip
                                                placement="top"
                                                title={task.TaskName?.length! > 150 ? task.TaskName : ''}
                                            >
                                                <Typography
                                                    component={'span'}
                                                    sx={{
                                                        ...ellipsisStyles,
                                                        cursor: 'default',
                                                        minWidth: task.TaskName?.length! > 50 ? '150px' : 'max-content',
                                                    }}
                                                >
                                                    {task.TaskName?.split('@')[0]}
                                                </Typography>
                                            </Tooltip>
                                        }
                                        align="left"
                                    />
                                )}

                                {isEditing ? (
                                    <CellOfRow
                                        value={
                                            <Stack width={'180px'}>
                                                <Controller
                                                    name="status"
                                                    control={control}
                                                    rules={{
                                                        required: true,
                                                    }}
                                                    render={({ field, fieldState: { error } }) => {
                                                        const filteredOptions =
                                                            mode === Mode.create
                                                                ? cameraStatusTask.filter(
                                                                      (option) =>
                                                                          option.Value !== TaskStatus.Completed &&
                                                                          option.Value !== TaskStatus.Cancelled
                                                                  )
                                                                : cameraStatusTask;

                                                        const valueStatusTask = cameraStatusTask.find(
                                                            (option) => option.Value === field.value
                                                        )
                                                            ? field.value
                                                            : null;

                                                        return (
                                                            <Autocomplete
                                                                {...field}
                                                                options={filteredOptions.map((option) => option.Value)}
                                                                value={valueStatusTask}
                                                                getOptionLabel={(option) =>
                                                                    mappingStatusTask[option] || ''
                                                                }
                                                                renderInput={(params) => (
                                                                    <TextField
                                                                        {...params}
                                                                        InputLabelProps={{
                                                                            shrink: true,
                                                                        }}
                                                                        label={
                                                                            <span>
                                                                                Status
                                                                                <span
                                                                                    style={{
                                                                                        color: 'red',
                                                                                    }}
                                                                                >{`*`}</span>
                                                                            </span>
                                                                        }
                                                                        placeholder="Select status"
                                                                    />
                                                                )}
                                                                clearIcon={<></>}
                                                                sx={{ ...autocompleteStyles }}
                                                                onChange={(_, val) => {
                                                                    field.onChange(val);
                                                                }}
                                                            />
                                                        );
                                                    }}
                                                />
                                            </Stack>
                                        }
                                    />
                                ) : (
                                    <StatusPriorityDropdown
                                        task={task}
                                        type={DropdownType.Status}
                                        label={task.TaskStatus}
                                        menuItems={
                                            task.TaskStatus !== TaskStatus.Cancelled
                                                ? [
                                                      TaskStatus.Planned,
                                                      TaskStatus.Allocated,
                                                      TaskStatus.InProgress,
                                                      TaskStatus.Completed,
                                                      TaskStatus.Cancelled,
                                                  ]
                                                : [TaskStatus.Planned]
                                        }
                                        handleChangeStatusOrPriority={handleChangeStatusOrPriority}
                                    />
                                )}

                                {isEditing ? (
                                    <CellOfRow
                                        value={
                                            <Stack width={'180px'}>
                                                <Controller
                                                    name="priority"
                                                    control={control}
                                                    render={({ field, fieldState: { error } }) => {
                                                        const valuePriorityTask = cameraPriorityTask.find(
                                                            (option) => option.Value === field.value
                                                        )
                                                            ? field.value
                                                            : null;

                                                        return (
                                                            <Autocomplete
                                                                {...field}
                                                                options={cameraPriorityTask.map(
                                                                    (option) => option.Value
                                                                )}
                                                                value={valuePriorityTask}
                                                                getOptionLabel={(option) =>
                                                                    mappingPriorityTask[option] || ''
                                                                }
                                                                renderInput={(params) => (
                                                                    <TextField
                                                                        {...params}
                                                                        InputLabelProps={{
                                                                            shrink: true,
                                                                        }}
                                                                        label={<span>Priority</span>}
                                                                        placeholder="Select priority"
                                                                    />
                                                                )}
                                                                clearIcon={<></>}
                                                                sx={{ ...autocompleteStyles }}
                                                                onChange={(_, val) => {
                                                                    field.onChange(val);
                                                                }}
                                                            />
                                                        );
                                                    }}
                                                />
                                            </Stack>
                                        }
                                    />
                                ) : (
                                    <StatusPriorityDropdown
                                        task={task}
                                        type={DropdownType.Priority}
                                        label={task.TaskPriority}
                                        menuItems={[TaskPriority.Low, TaskPriority.Medium, TaskPriority.High]}
                                        handleChangeStatusOrPriority={handleChangeStatusOrPriority}
                                    />
                                )}

                                {isEditing ? (
                                    <CellOfRow
                                        value={
                                            <Stack width={'100%'} direction={'column'} spacing={1}>
                                                <Controller
                                                    name="assign"
                                                    control={control}
                                                    render={({ field, fieldState: { error } }) => (
                                                        <TextField
                                                            {...field}
                                                            placeholder={'Assignee'}
                                                            label="Assignee"
                                                            error={!!error?.message}
                                                            helperText={error?.message}
                                                            value={field.value}
                                                        />
                                                    )}
                                                />
                                            </Stack>
                                        }
                                        align="center"
                                    />
                                ) : (
                                    <CellOfRow
                                        value={
                                            <Tooltip
                                                placement="top"
                                                title={task.Assignee?.length! > 150 ? task.Assignee : ''}
                                            >
                                                <Typography
                                                    component={'span'}
                                                    sx={{
                                                        ...ellipsisStyles,
                                                        cursor: 'default',
                                                        minWidth: task.Assignee?.length! > 50 ? '150px' : 'max-content',
                                                    }}
                                                >
                                                    {task.Assignee?.split('@')[0]}
                                                </Typography>
                                            </Tooltip>
                                        }
                                        align="left"
                                    />
                                )}

                                {isEditing ? (
                                    <CellOfRow
                                        value={
                                            <Stack width={'100%'} direction={'column'} spacing={1}>
                                                <Controller
                                                    name="endDate"
                                                    control={control}
                                                    rules={{
                                                        required: {
                                                            value: true,
                                                            message: 'Please choose a date.',
                                                        },
                                                    }}
                                                    render={({ field, fieldState: { error } }) => (
                                                        <DesktopDatePicker
                                                            {...field}
                                                            label={
                                                                <span>
                                                                    Due date
                                                                    <span style={{ color: 'red' }}>{`*`}</span>
                                                                </span>
                                                            }
                                                            value={field.value}
                                                            format="DD/MM/YYYY"
                                                            shouldDisableDate={(date) =>
                                                                validateDatePickerValue(moment(date).toDate()) !== null
                                                            }
                                                            slotProps={{
                                                                textField: {
                                                                    inputProps: {
                                                                        readOnly: true,
                                                                    },
                                                                    fullWidth: true,
                                                                },
                                                            }}
                                                        />
                                                    )}
                                                />
                                            </Stack>
                                        }
                                        align="center"
                                    />
                                ) : (
                                    <CellOfRow
                                        value={<OverdueButton taskStatus={task.TaskStatus} endDate={task.EndDate} />}
                                        align="center"
                                    />
                                )}

                                <CellOfRow
                                    value={
                                        <Typography component={'span'} sx={{ wordBreak: 'break-all' }}>
                                            {task.CreatedBy?.split('@')[0]}
                                        </Typography>
                                    }
                                    align="left"
                                />

                                <CellOfRow
                                    value={<Typography component={'span'}>{formatUKDate(task.Created!)}</Typography>}
                                    align="center"
                                />

                                <CellOfRow
                                    value={
                                        <>
                                            {task.TaskStatus !== TaskStatus.Completed && (
                                                <ActionButtons
                                                    task={task}
                                                    isEditing={isEditing}
                                                    disabled={disabled}
                                                    handleSubmit={handleSubmit}
                                                    onSetListTask={onSetListTask}
                                                    handleConfirmUpsertTask={handleConfirmUpsertTask}
                                                    setMode={setMode}
                                                    setTaskItem={setTaskItem}
                                                />
                                            )}
                                        </>
                                    }
                                    align="right"
                                />
                            </TableRow>
                        );
                    })}
                </TableBody>
            </Table>
        </TableContainer>
    );
}
